import { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import {
  Grid as MuiGrid,
  Dialog as MuiDialog,
  Button as MuiButton,
  TextField as MuiTextField,
  DialogTitle as MuiDialogTitle,
  Autocomplete as MuiAutocomplete,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
} from "@mui/material";
import { useAlerts } from "common";
import { patchNotificationStatus } from "app/services/expensesService";
import { ExpensesNotification } from "app/shared/constants";

export const NotificationStatus = ({
  openDialog,
  setOpenDialog,
  statusToChange,
  expenseId,
  setExpenses,
  expenseIndex,
}) => {
  const [open, setOpen] = useState(openDialog);
  const [, setLoading] = useState(false);
  const { t } = useTranslation();
  const { clearAlert, setAlert } = useAlerts();
  const notificationDialogStr =
    "UploadedExpenses.mainView.list.notificationStatusDialog";
  const handleClose = () => {
    setOpen(false);
    setOpenDialog(false);
  };
  const getDefaultValue = (statusToChange) => {
    switch (statusToChange) {
      case "UNRESOLVED": {
        return t(ExpensesNotification["INPROCESS"]);
      }
      case "INPROCESS": {
        return t(ExpensesNotification["RESOLVED"]);
      }
      default:
        return "";
    }
  };

  // Controllable State for Notification Status
  const [notificationStatus, setNotificationStatus] = useState(
    getDefaultValue(statusToChange)
  );

  const getNotificationOptions = (statusToChange) => {
    switch (statusToChange) {
      case "UNRESOLVED": {
        return [
          t(ExpensesNotification["INPROCESS"]),
          t(ExpensesNotification["RESOLVED"]),
        ];
      }
      case "INPROCESS": {
        return [t(ExpensesNotification["RESOLVED"])];
      }
      default:
        return [];
    }
  };

  const updateNotificationStatus = (id, values) => {
    patchNotificationStatus(
      id,
      values,
      setLoading,
      setOpenDialog,
      setExpenses,
      expenseIndex,
      clearAlert,
      setAlert
    );
  };

  const formData = {
    notificationStatus,
    notes: "",
  };
  useEffect(() => {
    setOpen(openDialog);
  }, [openDialog]);
  return (
    <Formik
      initialValues={formData}
      validationSchema={notificationStatusValidation}
    >
      {(props) => {
        const {
          values,
          handleChange,
          handleSubmit,
          setFieldValue,
          dirty,
          isValid,
        } = props;
        return (
          <form onSubmit={handleSubmit}>
            <MuiDialog open={open} fullWidth maxWidth="xs">
              <MuiDialogTitle>
                {t(`${notificationDialogStr}.title`)}
              </MuiDialogTitle>
              <MuiDialogContent>
                <MuiGrid size={12}
                  container
                  spacing={2}
                  sx={{
                    paddingTop: 1,
                  }}
                >
                  <MuiGrid size={12}>
                    <MuiAutocomplete
                      id="notificationStatus"
                      options={getNotificationOptions(statusToChange)}
                      getOptionLabel={(option) => option}
                      value={
                        notificationStatus || getDefaultValue(statusToChange)
                      }
                      onChange={(event, value) => {
                        setNotificationStatus(value);
                        setFieldValue("notificationStatus", value);
                      }}
                      clearIcon={false}
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          label={`${t(
                            `${notificationDialogStr}.fields.notificationStatus.label`
                          )}`}
                          variant={"outlined"}
                        />
                      )}
                    />
                  </MuiGrid>
                  <MuiGrid size={12}>
                    <MuiTextField
                      id="notes"
                      label={`${t(
                        `${notificationDialogStr}.fields.notes.label`
                      )}`}
                      onChange={handleChange}
                      multiline
                      variant={"outlined"}
                      autoComplete="off"
                      fullWidth
                      required
                      slotProps={{
                        htmlInput: { maxLength: 30 },
                      }}
                    />
                  </MuiGrid>
                </MuiGrid>
              </MuiDialogContent>
              <MuiDialogActions>
                <MuiButton onClick={handleClose} color="primary">
                  {t(`${notificationDialogStr}.actionButtons.dontChange.label`)}
                </MuiButton>
                <MuiButton
                  color="primary"
                  onClick={() => {
                    updateNotificationStatus(expenseId, values);
                  }}
                  disabled={!dirty || !isValid}
                >
                  {t(`${notificationDialogStr}.actionButtons.change.label`)}
                </MuiButton>
              </MuiDialogActions>
            </MuiDialog>
          </form>
        );
      }}
    </Formik>
  );
};

const notificationStatusValidation = Yup.object().shape({
  notificationStatus: Yup.string().required(),
  notes: Yup.string().required(),
});
